import { assert } from '@ember/debug';
import { getOwner, setOwner } from '@ember/application';
import { task, rawTimeout } from 'ember-concurrency';
import hasEqualUrls from 'ember-stereo/-private/utils/has-equal-urls';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import { sub } from 'date-fns';
import { numericDuration } from 'ember-stereo/helpers/numeric-duration';
import { registerDestructor, isDestroyed as emberIsDestroyed } from '@ember/destroyable';

export default class Feed {
  @service store;
  @service socket;
  @service session;
  @service stereo;
  @service feeds;
  @service playback;
  @service clock;
  @service settings;

  @tracked airing;
  @tracked nextAiring;
  @tracked note;
  @tracked track;
  @tracked item;
  @tracked itemType;
  @tracked broadcast;
  @tracked station;
  @tracked show;
  @tracked isLoaded = false
  @tracked itemsLastFetchedAt;

  @tracked isDestroyed = false;

  constructor(feedableObject, options = { }) {
    assert("owner is required to create a feed object", options.owner);
    try {
      let owner = getOwner(options.owner) || getOwner(feedableObject)
      setOwner(this, owner);
    } catch(e) {
      console.error(e)
      throw new Error('error while setting owner on feed object')
    }

    registerDestructor(this, () => {
      this.isDestroyed = true;
      this.airingCableConnection?.unsubscribe();
      if (this.url) {
        this.stereo?.unregisterMediaSessionActions?.(this.url);
      }
    });
    this.feedObject = feedableObject;
  }

  // Station DMCA tolerance: may listeners seek/scrub within archived playback?
  // Resolved from the station's `playback/scrubbing` tolerance; defaults to
  // allowed when the station hasn't set it.
  get canScrub() {
    return this.settings?.features?.['playback/scrubbing'] !== false;
  }

  // The scrubber and its controls read their mode from the FEED, never from the
  // backing sound directly — the sound's seekability varies by stereo connection
  // and is briefly false on mobile while a freshly-switched sound loads. A
  // live-edge feed (a live broadcast) pins the playhead to the live edge and
  // rewinds into a recorded PlaybackFeed; every other feed scrubs its own archive.
  get isLiveEdge() {
    return false;
  }

  // Can the listener rewind / fast-forward this feed right now? A feed that plays a
  // finite archive delegates to its transport; a live-edge broadcast overrides
  // these (rewind = jump into recorded playback; nothing ahead of live to
  // fast-forward to). See BroadcastFeed.
  get isRewindable() {
    return this.transportIsRewindable;
  }

  get isFastForwardable() {
    return this.transportIsFastForwardable;
  }

  get imageSource() {
    // given the airing, show, and station, find the image in that order
    if (this.airing?.image?.small) {
      return this.airing.image
    } else if (this.show?.image?.small) {
      return this.show.image
    } else if (this.airing?.show?.image?.small) {
      return this.airing.show.image
    } else if (this.station?.image?.small) {
      return this.station.image
    }
  }

  get startsAt() {
    return this.airing?.startsAt
  }

  get endsAt() {
    return this.airing?.endsAt
  }

  // The airing's broadcast slot hasn't started yet — there's nothing to play, so
  // the UI shows an "Airs in …" countdown instead of a scrubber. Reads the clock
  // so it flips live the moment the airing starts.
  get isFuture() {
    return !!this.airing?.startsAt && this.airing.startsAt > this.clock.date;
  }

  get stationId() {
    return this.station?.uid
  }

  get broadcastId() {
    return this.broadcast?.id
  }

  get loadedSound() {
    if (this.identifier) {
      return this.stereo.findSound(this.identifier);
    }
  }

  // ---- Transport contract ----------------------------------------------------
  // One timeline the UI reads, regardless of where audio is actually playing.
  // The Sound is identity-stable: while casting, ember-stereo swaps its backing
  // connection to an AirPlay outlet, so `loadedSound` transparently reports the
  // remote device's clock and play-state — no local/remote fork here. All in ms.

  get transportPosition() {
    return this.loadedSound?.position;
  }

  get transportDuration() {
    return this.loadedSound?.duration;
  }

  get transportIsPlaying() {
    return this.loadedSound?.isPlaying;
  }

  get transportIsSeekable() {
    return this.loadedSound?.isSeekable;
  }

  get transportIsRewindable() {
    return this.loadedSound?.isRewindable;
  }

  get transportIsFastForwardable() {
    return this.loadedSound?.isFastForwardable;
  }

  // A seek this close to where the sound already sits isn't worth a buffer flush.
  static SEEK_TOLERANCE_MS = 2000;

  // How long to keep painting a seek's target before giving up on it and
  // following the transport again, so a seek that never lands can't freeze the
  // scrubber.
  static SEEK_ARRIVAL_TIMEOUT_MS = 10000;

  // Where the playhead is HEADED — a requested start offset, or a seek still in
  // flight — until the transport actually gets there. See playheadPositionMs.
  @tracked pendingPositionMs = null;

  seek(positionMs) {
    this.seekTask.perform(positionMs);
  }

  // Seeking is a request, not an instant move. A connection can report a
  // nonsense position while it services one — hls.js discards its buffer and
  // briefly reports 0 before the new fragments land — so painting the transport
  // directly makes the scrubber snap to zero and then jump to where the listener
  // actually dropped the handle. Hold the target until the transport arrives.
  seekTask = task({ restartable: true }, async positionMs => {
    if (!this.loadedSound) return;

    this.pendingPositionMs = positionMs;
    this.loadedSound.position = positionMs;

    let waited = 0;
    while (waited < Feed.SEEK_ARRIVAL_TIMEOUT_MS) {
      await rawTimeout(100);
      waited = waited + 100;

      let position = this.transportPosition;
      if (
        Number.isFinite(position) &&
        Math.abs(position - positionMs) < Feed.SEEK_TOLERANCE_MS
      ) {
        break;
      }
    }

    this.pendingPositionMs = null;
  });

  get hasItems() {
    return this.airing?.publicItemsCount > 0
  }

  get itemsFetched() {
    return !!this.itemsLastFetchedAt;
  }

  get itemsLoading() {
    return this.loadAiringItemsTask.isRunning;
  }

  get items() {
    if (!this.itemsFetched) {
      next(this, () => { 
        this.loadAiringItemsTask.perform()
      });
    }

    if (this.airing) {
      return _.compact([...this.airing.notes, ...this.airing.tracks]);;
    } else {
      return []
    }
  }

  playPauseTask = task({ restartable: true, maxConcurrency: 1 }, async (trackStartTime = 0) => {
    if (this.loadedSound?.isResolved) {
      // This feed has a backing connection — toggle it. The Sound abstracts
      // local vs. cast: togglePause reaches the AirPlay outlet while casting.
      // (Keyed on isResolved, not isLoaded: the AirPlay connection adopts an
      // already-loaded outlet and may never fire its own 'loaded'.)
      this.loadedSound.togglePause();
    } else {
      // A different / not-yet-loaded feed — start it. While casting, the feed
      // switch is picked up by the service, which re-points the outlet here.
      await this.playTask.linked().perform(trackStartTime);
    }
  });

  loadAiringItemsTask = task({drop: true}, async () => {
    // Guard every iteration: the owner (ApplicationInstance) may be torn
    // down mid-loop, which would otherwise make `this.settings` throw with
    // "Cannot call .lookup(...) after the owner has been destroyed".
    while (!this.airing || !this._settingsIsLoaded()) {
      if (this.isDestroyed || emberIsDestroyed(this)) return;
      await rawTimeout(10);
    }
    if (this.isDestroyed || emberIsDestroyed(this)) return;

    let airingId = this.airing.id;

    // Load tracks and notes additively rather than via `include`, which would
    // canonically replace airing.tracks / airing.notes and wipe records the
    // timeline loaded separately (e.g. queued tracks). The host app's adapter
    // routes these to the client (v1) or public endpoint as appropriate.
    let [airing] = await Promise.all([
      this.store.queryRecord('airing', { id: airingId, reload: true }),
      this.store.query('track', { filter: { airing_id: airingId }, reload: true }),
      this.store.query('note', { filter: { airing_id: airingId }, reload: true }),
    ]);

    if (this.isDestroyed || emberIsDestroyed(this)) return;

    this.itemsLastFetchedAt = new Date();
    this.airing = airing;

    if (!this.airingCableConnection) {
      this.loadAiringUpdatesCableTask.perform();
    }
  });

  _settingsIsLoaded() {
    try {
      return this.settings?.isLoaded;
    } catch (e) {
      return false;
    }
  }

  loadAiringUpdatesCableTask = task({ maxConcurrency: 1, restartable: true }, async () => {
    this.airingCableConnection = this.socket.subscribe(
      {
        channel: 'SignalChannel',
        station_id: this.station.id,
        airing_id: this.airing.id,
      },
      {
        received: (data) => {
          if (this.isDestroyed || emberIsDestroyed(this)) return;
          if (this.settings.debug) {
            console.log(
              '⚡️ received SignalChannel update for playback feed, handling...', data
            );
          }
          if (this.itemsFetched) { // if we've already loaded this items, that means we're using the items
            this.loadAiringItemsTask.perform()
          } else {
            this.airing.reload();
          }
          this.lastCacheKey = data?.cache;
        },
      }
    );
  });

  get normalizedIdentifier() { // This has query params and tokens stripped from it
    return normalizeIdentifier(this.url)
  }

  get identifier() {
    return this.url;
  }

  // Can this feed be sent to a remote playback device (AirPlay/Chromecast)?
  // Sync so the UI can show/hide the control before anything plays.
  get canRemotePlay() {
    return false;
  }

  // A URL a remote device can fetch on its own (no session auth headers).
  // Null when one isn't available synchronously — see fetchRemotePlaybackUrl.
  get remotePlaybackUrl() {
    return null;
  }

  // Resolve the remote-playback URL, fetching it on demand if needed. Kept off
  // the default payload because it can be a bearer-token URL.
  async fetchRemotePlaybackUrl() {
    return this.remotePlaybackUrl;
  }

  // Extra load options for the current cast state. While casting, ember-stereo
  // only resolves a sound to the device if it's handed the device-fetchable URL,
  // so a feed switch routes straight to the device (no local playback first).
  // Inert when not casting or when this feed has no remote URL.
  async castLoadOptions(trackStartTime) {
    if (!this.stereo.isCasting) {
      return {};
    }
    let castUrl = await this.fetchRemotePlaybackUrl();
    if (!castUrl) {
      return {};
    }
    let options = { castUrl };
    // The device loads the cast URL at this position (LoadRequest.currentTime).
    // A post-load seek on a just-started receiver isn't reliable, so a feed
    // switch onto an archive must carry its start offset here or it plays from 0.
    let castStartTime = this.castStartTime(trackStartTime);
    if (castStartTime != null) {
      options.castStartTime = castStartTime;
    }
    return options;
  }

  // Position (ms) a freshly-cast feed should begin at. Live feeds start at the
  // live edge (no offset); a seekable archive overrides this with its resume/
  // requested position.
  castStartTime() {
    return null;
  }

  // Where a freshly-loaded sound should BEGIN, in seconds. Handed to the
  // connection at construction (hls.js `startPosition`) so the first fragment it
  // fetches is the one we want — loading at 0 and seeking afterwards throws away
  // the buffer it just filled and refetches, which is the pause a listener hears
  // when rewinding off the live edge. Live feeds have no offset.
  loadStartPosition() {
    return null;
  }

  // How long this feed's sound should hold a paused stream's connection open
  // (ms). Only meaningful for a live stream — a finite archive isn't holding a
  // connection to anything. See BroadcastFeed.
  loadStreamPauseGraceMs() {
    return null;
  }

  // The timeline the OS media controls should show, when this feed knows a
  // better one than its audio does — an airing's wall-clock window rather than
  // however much of it happens to be recorded. Same coordinates as the in-app
  // scrubber, so the lock screen and the player agree. {duration, position} ms.
  get timeline() {
    return null;
  }

  // How the OS media controls should behave for this feed, where "what the sound
  // does" is the wrong answer — see BroadcastFeed, where seeking back means
  // leaving the live stream for its recording. Handlers close over the feed, not
  // over an airing or a position, so they always act on current state.
  get mediaSessionActions() {
    return null;
  }

  // Registered whenever this feed is played or loaded — NOT only when a load
  // actually happens. A feed that finds its sound already resolved does no work
  // but is still the thing the OS controls now point at, and an unregistered
  // live feed falls back to the library's default seekto: seeking the live
  // stream in place instead of dropping into its recording.
  applyMediaSessionActions() {
    let actions = this.mediaSessionActions;
    if (!actions || !this.url) return;

    this.stereo?.registerMediaSessionActions?.(this.url, actions);
  }

  // Where the playhead is, for anything painting a scrubber: where we're headed
  // if a seek or a positioned load is still in flight, otherwise wherever the
  // transport actually is. Painting this rather than the transport keeps the
  // handle still through a seek and through a switch between feeds, both of
  // which briefly have no trustworthy position to report.
  get playheadPositionMs() {
    if (this.pendingPositionMs != null) {
      return this.pendingPositionMs;
    }
    return Number.isFinite(this.transportPosition) ? this.transportPosition : 0;
  }

  // The shared cast-aware load DECISION, in one place. Returns the
  // `[url, options]` to perform through the stereo service when this feed needs
  // (re)loading — i.e. it has no backing connection yet, OR we're casting (so
  // returning to a feed re-resolves it onto the device, threading the device
  // castUrl). Returns `null` when no (re)load is needed. Subclasses provide only
  // their connection list / metadata / xhr; the cast logic lives here so a cast
  // fix lands once instead of in every feed's playTask/loadTask.
  //
  // NOTE: callers run the actual `stereoTask.linked().perform(...args)` themselves
  // — ember-concurrency's task linking anchors to the running task at call time,
  // so performing from inside this async helper would silently break the link.
  async castAwareLoadArgs(trackStartTime, { useConnections, metadata = {}, xhr }) {
    this.applyMediaSessionActions();

    if (this.loadedSound?.isResolved && !this.stereo.isCasting) {
      return null;
    }
    let castOptions = await this.castLoadOptions(trackStartTime);
    let loadOptions = { useConnections, metadata, xhr, ...castOptions };

    let startPosition = this.loadStartPosition(trackStartTime);
    if (startPosition != null) {
      loadOptions.startPosition = startPosition;
    }

    let streamPauseGraceMs = this.loadStreamPauseGraceMs();
    if (streamPauseGraceMs != null) {
      loadOptions.streamPauseGraceMs = streamPauseGraceMs;
    }

    return [this.url, loadOptions];
  }

  matches(params = {}) {
    if ('identifier' in params) {
      return hasEqualUrls(normalizeIdentifier(params.identifier), this.normalizedIdentifier)
    } else if ('url' in params) {
      return hasEqualUrls(normalizeIdentifier(params.url), this.normalizedIdentifier)
    } else if ('feedRecord' in params) {
      return params.feedRecord?.id === this.feedObject?.id
    } else if ('airing' in params) {
      return params.airing?.id === this.airing?.id
    } else if (('showId' in params) && ('episodeIndex' in params) && params.episodeIndex) {
      let matchingId = String(this.show?.id) === String(params.showId) || String(this.show?.slug) === String(params.showId);
      return matchingId && String(this.episodeIndex) === String(params.episodeIndex);
    } else if (('showId' in params)) {
      let matchingId = String(this.show?.id) === String(params.showId) || String(this.show?.slug) === String(params.showId);
      return matchingId && String(this.episodeIndex) === String(0);
    }
  }

  get currentPercentageElapsed() {
    if (this.isLive && this.airing) {
      let currentPosition = this.clock.date.getTime() - this.airing.startsAt.getTime();
      return (
        (currentPosition / (this.airing.endsAt.getTime() - this.airing.startsAt.getTime())) *
        100
      );
    } else if (
      this.airing &&
      this.clock.date >= this.airing.startsAt &&
      this.clock.date <= this.airing.endsAt &&
      this.loadedSound?.isSeekable
    ) {
      let currentPosition = this.clock.date.getTime() - this.airing.startsAt.getTime();

      return (
        (currentPosition / (this.airing.endsAt.getTime() - this.airing.startsAt.getTime())) *
        (this.loadedSound?.position / this.loadedSound?.duration) *
        100
      );
    } else if (this.loadedSound) {
      return (this.transportPosition / this.transportDuration) * 100;
    } else {
      return 0;
    }
  }

  get timeRemaining() {
    if (this.isLive) {
      let remaining =
        new Date(this.airing.endsAt).getTime() -
        this.clock.date.getTime();
      return numericDuration([remaining]);
    } else if (this.loadedSound) {
      let remaining = this.transportDuration - this.transportPosition;
      return numericDuration([remaining]);
    } else {
      return 0;
    }
  }

  get timeElapsed() {
    if (this.isLive && this.airing) {
      let elapsed =
        this.clock.date.getTime() -
        new Date(this.airing.startsAt).getTime();
      return numericDuration([elapsed]);
    } else if (this.loadedSound) {
      return numericDuration([this.transportPosition]);
    } else {
      return 0;
    }
  }
}
